import { useRef } from 'react';
import { useDrop } from 'react-dnd';
import type { ValSelectProps } from '../types';
import { ValueSelector } from './ValueSelector';

export const ValSelector = ({
  value,
  handleOnChange,
  title,
  className,
  type,
  values,
  fieldData,
  disabled,
  testID,
  debugMode,
  onLog,
  ...props
}: ValSelectProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const placeHolderText = fieldData?.placeholder ?? '';
  const inputTypeCoerced = type === 'number' ? 'number' : 'text';

  const [, drop] = useDrop({
    accept: 'tableRow',
    drop: (item: { value: string; path: string; bsonType: string }) => {
      // log drop item
      if (debugMode && onLog) onLog('Rule useDrop', { item });
      handleChange(item.value);
      inputRef?.current?.focus();
    },
  });

  switch (type) {
    case 'select':
    case 'multiselect':
      return (
        <ValueSelector
          {...props}
          testID={testID}
          className={className}
          title={title}
          handleOnChange={handleOnChange}
          disabled={disabled}
          value={value}
          options={values!}
          multiple={type === 'multiselect'}
          debugMode={debugMode}
          onLog={onLog}
        />
      );

    case 'textarea':
      return (
        <textarea
          data-testid={testID}
          placeholder={placeHolderText}
          value={value}
          title={title}
          className={className}
          disabled={disabled}
          onChange={e => handleOnChange(e.target.value)}
        />
      );

    case 'boolean':
      return (
        <input
          data-testid={testID}
          type="checkbox"
          className={className}
          title={title}
          onChange={e => handleOnChange(e.target.checked)}
          checked={!!value}
          disabled={disabled}
        />
      );

    case 'radio':
      return (
        <span data-testid={testID} className={className} title={title}>
          {values &&
            values.map(v => (
              <label key={v.name}>
                <input
                  type="radio"
                  value={v.name}
                  disabled={disabled}
                  checked={value === v.name}
                  onChange={e => handleOnChange(e.target.value)}
                />
                {v.label}
              </label>
            ))}
        </span>
      );
  }

  const handleChange = (value: any) => {
    if (type === 'number') {
      handleOnChange(Number(value));
    } else {
      handleOnChange(value);
    }
  };

  return (
    <span ref={drop} style={{ display: 'contents' }}>
      <input
        ref={inputRef}
        data-testid={testID}
        type={inputTypeCoerced}
        placeholder={placeHolderText}
        value={value}
        title={title}
        disabled={disabled}
        className={className}
        onChange={e => handleChange(e.target.value)}
      />
    </span>
  );
};

ValSelector.displayName = 'ValSelector';
